import { useState } from "react";

import { Droplet, Package } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";

import { IngredientTransferForm } from "./IngredientTransferForm";
import { ProductTransferForm } from "./ProductTransferForm";

interface NewTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type TransferKind = "ingredient" | "product";

// Single "Nuevo Traslado" dialog with a type selector: both ingredientes and
// producto terminado move between plantas (P1 <-> P2 <-> NC). For PT this covers
// stock already ingresado a bodega and lotes producidos aún en planta alike.
export function NewTransferDialog({
  open,
  onOpenChange,
  onSuccess,
}: NewTransferDialogProps) {
  const [kind, setKind] = useState<TransferKind>("ingredient");

  const handleOpenChange = (next: boolean) => {
    if (!next) setKind("ingredient");
    onOpenChange(next);
  };

  const handleDone = () => {
    onSuccess?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Nuevo Traslado</DialogTitle>
          <DialogDescription>
            Selecciona qué vas a trasladar y registra el movimiento para
            trazabilidad.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={kind}
          onValueChange={(v) => setKind(v as TransferKind)}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="ingredient" className="gap-1.5">
              <Droplet className="h-3.5 w-3.5" />
              Ingredientes (P1 ↔ P2 ↔ NC)
            </TabsTrigger>
            <TabsTrigger value="product" className="gap-1.5">
              <Package className="h-3.5 w-3.5" />
              Producto Terminado (P1 ↔ P2 ↔ NC)
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="ingredient"
            className="duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
          >
            <IngredientTransferForm onDone={handleDone} />
          </TabsContent>
          <TabsContent
            value="product"
            className="duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
          >
            <ProductTransferForm onDone={handleDone} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
